var _react_refresh_temp_1;
_react_refresh_temp_1 = $RefreshSig$();
import { useCallback, useEffect, useMemo, useReducer, useRef, } from 'react';
import { ARROW_DOWN_KEY, ARROW_LEFT_KEY, ARROW_RIGHT_KEY, ARROW_UP_KEY, BACKSPACE_KEY, DELETE_KEY, ENTER_KEY, ESCAPE_KEY, } from '#constants';
import { COMBOBOX_FOCUS_GUARD_MS, ComboboxActionType, } from './Combobox.constants';
import { comboboxReducer } from './Combobox.reducer';
import { searchByContainsTerm } from './services';
import { optionIdFactory } from './services/ComboboxIdFactory';
export const useCombobox = ({ id, isMultiselect, onBlur, onChange, onFocus, options, readOnly, ref, searchFn = searchByContainsTerm, value, }) => {
    _react_refresh_temp_1();
    const containerRef = useRef(null);
    const currentRef = useRef(null);
    const inputRef = ref;
    const [state, dispatch] = useReducer(comboboxReducer(isMultiselect), (isMultiselect
        ? {
            currentOptionIndex: -1,
            currentSelectedOptionIndex: -1,
            currentStyledOptionIndex: -1,
            displayValue: '',
            filteredOptions: options,
            hasOptions: Boolean(options.length),
            innerValue: value,
            isExpanded: false,
            isFocused: false,
            options,
            search: '',
        }
        : {
            currentOptionIndex: -1,
            displayValue: options.find((option) => option.id === value)?.text || '',
            filteredOptions: options,
            hasOptions: Boolean(options.length),
            innerValue: value,
            isExpanded: false,
            isFocused: false,
            options,
            search: '',
        }));
    const expand = useCallback(() => dispatch({ type: ComboboxActionType.EXPAND }), []);
    const collapse = useCallback(() => dispatch({ type: ComboboxActionType.COLLAPSE }), []);
    const select = useCallback((option) => {
        dispatch({
            type: ComboboxActionType.OPTION_MOUSE_CLICK,
            value: option,
        });
    }, []);
    const shouldPreventInputBlur = useRef(false);
    const keepInputFocus = useCallback(() => {
        shouldPreventInputBlur.current = true;
        setTimeout(() => {
            shouldPreventInputBlur.current = false;
        }, COMBOBOX_FOCUS_GUARD_MS);
    }, []);
    const onTagClick = useCallback((index) => {
        dispatch({
            type: ComboboxActionType.CHANGE_CURRENT_SELECTED_OPTION,
            value: index,
        });
        requestAnimationFrame(() => {
            inputRef.current?.focus();
        });
    }, [inputRef]);
    /**
     * Input onBlur is called before opening button's
     * onClick and 'isExpanded' is set to false thus after
     * trying to click the arrow and close the combobox
     * the toggle handler reacts as if the combobox is
     * closed and expands it. To handle this we use a flag
     * that expires after a short timeout, to prevent the
     * arrow button onClick to expand the combobox again
     * when trying to close it.
     */
    const isBlurRef = useRef(false);
    const onInputBlur = useCallback((event) => {
        if (shouldPreventInputBlur.current) {
            // Fix focus not being called in Firefox:
            requestAnimationFrame(() => {
                inputRef.current?.focus();
            });
            return;
        }
        if (onBlur) {
            onBlur(event);
        }
        isBlurRef.current = true;
        setTimeout(() => {
            isBlurRef.current = false;
        }, COMBOBOX_FOCUS_GUARD_MS);
        dispatch({ type: ComboboxActionType.BLUR });
    }, [inputRef, onBlur]);
    const toggle = useCallback(() => {
        if (readOnly) {
            return;
        }
        if (state.isExpanded) {
            collapse();
        }
        else {
            if (!isBlurRef.current) {
                expand();
            }
        }
    }, [collapse, expand, readOnly, state.isExpanded]);
    const onInputChange = useCallback((event) => {
        dispatch({
            type: ComboboxActionType.SEARCH,
            value: event.target.value,
            searchFn,
        });
    }, [searchFn]);
    const onInputFocus = useCallback((event) => {
        if (onFocus) {
            onFocus(event);
        }
        dispatch({
            type: ComboboxActionType.FOCUS,
        });
    }, [onFocus]);
    const onInputKeyDown = useCallback((event) => {
        if (readOnly) {
            return;
        }
        switch (event.key) {
            case ENTER_KEY: {
                dispatch({
                    type: ComboboxActionType.ENTER_KEY_PRESS,
                });
                /**
                 * Prevent triggering the form's onSubmit
                 */
                event.preventDefault();
                event.stopPropagation();
                break;
            }
            case ARROW_DOWN_KEY: {
                if (event.altKey) {
                    dispatch({
                        type: ComboboxActionType.EXPAND,
                    });
                }
                else {
                    dispatch({
                        type: ComboboxActionType.OPTIONS_DOWN,
                    });
                }
                /**
                 * Prevent page scroll
                 */
                event.preventDefault();
                event.stopPropagation();
                break;
            }
            case ARROW_UP_KEY: {
                if (event.altKey) {
                    dispatch({
                        type: ComboboxActionType.COLLAPSE,
                    });
                }
                else {
                    dispatch({
                        type: ComboboxActionType.OPTIONS_UP,
                    });
                }
                /**
                 * Prevent page scroll
                 */
                event.preventDefault();
                event.stopPropagation();
                break;
            }
            case ESCAPE_KEY: {
                dispatch({
                    type: ComboboxActionType.COLLAPSE,
                });
                /**
                 * Prevent closing modal when combo is in it
                 */
                event.preventDefault();
                event.stopPropagation();
                break;
            }
            case ARROW_LEFT_KEY: {
                if (isMultiselect) {
                    dispatch({
                        type: ComboboxActionType.SELECTED_OPTIONS_LEFT,
                    });
                }
                break;
            }
            case ARROW_RIGHT_KEY: {
                if (isMultiselect) {
                    dispatch({
                        type: ComboboxActionType.SELECTED_OPTIONS_RIGHT,
                    });
                }
                break;
            }
            case BACKSPACE_KEY: {
                if (isMultiselect) {
                    dispatch({
                        type: ComboboxActionType.BACKSPACE_SELECTED_OPTION,
                    });
                }
                break;
            }
            case DELETE_KEY: {
                if (isMultiselect) {
                    dispatch({
                        type: ComboboxActionType.DELETE_SELECTED_OPTION,
                    });
                }
                break;
            }
        }
    }, [isMultiselect, readOnly]);
    useEffect(() => {
        if (state.isExpanded) {
            inputRef.current?.focus();
        }
    }, [inputRef, state.isExpanded]);
    useEffect(() => {
        if (state.isExpanded) {
            currentRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            });
        }
    }, [state.currentOptionIndex, state.isExpanded]);
    useEffect(() => {
        if (onChange) {
            //@ts-expect-error - TODO: fix types
            onChange(state.innerValue);
        }
    }, [onChange, state.innerValue]);
    useEffect(() => {
        dispatch(isMultiselect
            ? {
                type: ComboboxActionType.SET_VALUE,
                value,
            }
            : {
                type: ComboboxActionType.SET_VALUE,
                value,
            });
    }, [isMultiselect, value]);
    const optionsHash = useMemo(() => options
        .map((option) => `${option.id}:${option.disabled}`)
        .join('.'), [options]);
    useEffect(() => {
        dispatch({
            type: ComboboxActionType.SET_OPTIONS,
            value: options,
        });
    }, [optionsHash]);
    return {
        containerRef,
        currentOptionId: state.filteredOptions[state.currentOptionIndex]
            ? optionIdFactory(id, state.currentOptionIndex)
            : '',
        currentOptionIndex: state.currentOptionIndex,
        currentSelectedOptionIndex: state.currentSelectedOptionIndex,
        currentStyledOptionIndex: state.currentStyledOptionIndex,
        currentRef,
        displayValue: state.displayValue,
        filteredOptions: state.filteredOptions,
        hasOptions: state.hasOptions,
        innerValue: state.innerValue,
        inputId: `combobox-${id}`,
        inputRef,
        isExpanded: state.isExpanded,
        isFocused: state.isFocused,
        keepInputFocus,
        listboxId: `listbox-${id}`,
        onInputBlur,
        onInputChange,
        onInputFocus,
        onInputKeyDown,
        onTagClick,
        search: state.search,
        select,
        toggle,
    };
};
_react_refresh_temp_1(useCombobox, "sjFb9ICJRKucORBshmDC0RgZtA8=");
